import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Settings as SettingsIcon, Upload, Copy, Trash2, Plus,
  Loader2, Check, UserPlus, Mail, Shield, Image, MessageSquare, Unlink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, isOwner } = useAuth();
  const [settings, setSettings] = useState({ brand_name: "Command Center", logo_url: null });
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandName, setBrandName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const fileRef = useRef(null);
  
  // Telegram state
  const [telegramLink, setTelegramLink] = useState(null);
  const [telegramCode, setTelegramCode] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [linkingTelegram, setLinkingTelegram] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [sRes, iRes, tRes] = await Promise.all([
        axios.get(`${API}/settings`),
        isOwner ? axios.get(`${API}/invites`) : Promise.resolve({ data: [] }),
        axios.get(`${API}/telegram/my-link`),
      ]);
      setSettings(sRes.data);
      setBrandName(sRes.data.brand_name || "Command Center");
      setInvites(iRes.data);
      setTelegramLink(tRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const linkTelegram = async () => {
    if (!telegramCode.trim()) {
      toast.error("Please enter the code from Telegram");
      return;
    }
    setLinkingTelegram(true);
    try {
      await axios.post(`${API}/telegram/link`, { link_code: telegramCode.trim() });
      toast.success("Telegram account linked!");
      setTelegramCode("");
      const tRes = await axios.get(`${API}/telegram/my-link`);
      setTelegramLink(tRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link Telegram");
    } finally {
      setLinkingTelegram(false);
    }
  };

  const directLinkTelegram = async () => {
    if (!telegramChatId.trim()) {
      toast.error("Please enter your Telegram Chat ID");
      return;
    }
    setLinkingTelegram(true);
    try {
      await axios.post(`${API}/telegram/admin/direct-link`, { 
        telegram_chat_id: parseInt(telegramChatId.trim()) 
      });
      toast.success("Telegram account linked!");
      setTelegramChatId("");
      const tRes = await axios.get(`${API}/telegram/my-link`);
      setTelegramLink(tRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link Telegram");
    } finally {
      setLinkingTelegram(false);
    }
  };

  const unlinkTelegram = async () => {
    try {
      await axios.delete(`${API}/telegram/unlink`);
      toast.success("Telegram account unlinked");
      setTelegramLink({ linked: false });
    } catch (e) {
      toast.error("Failed to unlink");
    }
  };

  const saveBrandName = async () => {
    try {
      const res = await axios.put(`${API}/settings`, { brand_name: brandName });
      setSettings(res.data);
      toast.success("Brand name updated");
      window.dispatchEvent(new Event("settings-updated"));
    } catch (e) { toast.error("Failed to save"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/settings/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettings((prev) => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success("Logo uploaded");
      window.dispatchEvent(new Event("settings-updated"));
    } catch (e) { toast.error("Failed to upload logo"); }
    finally { setUploading(false); }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/invites`, { email: inviteEmail || null });
      toast.success("Invite created");
      setInviteEmail("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const deleteInvite = async (id) => {
    try { await axios.delete(`${API}/invites/${id}`); toast.success("Invite deleted"); fetchAll(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    toast.success("Code copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const logoSrc = settings.logo_url && user?.company_id
    ? `${settings.logo_url}?company_id=${user.company_id}&t=${Date.now()}`
    : null;

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
          <SettingsIcon className="w-5 h-5 text-stone-50" />
        </div>
        <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Configuration</span>
      </div>
      <h1 data-testid="settings-title" className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
        Settings
      </h1>
      <p className="text-base text-stone-500 mb-8">Customize your Command Center and manage team access.</p>

      <div className="max-w-2xl space-y-6">
        {/* Branding */}
        <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardContent className="p-6">
            <h2 className="text-base font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Image className="w-4 h-4 text-stone-500" /> Branding
            </h2>
            {isOwner ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-stone-600">Brand Name</Label>
                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <Input
                      data-testid="brand-name-input"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder="Your business name"
                      className="flex-1"
                    />
                    <Button
                      data-testid="save-brand-btn"
                      onClick={saveBrandName}
                      disabled={brandName === settings.brand_name}
                      className="bg-stone-900 text-stone-50 hover:bg-stone-800 px-5 w-full sm:w-auto"
                    >
                      Save
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-600">Logo</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <div className="w-16 h-16 border-2 border-dashed border-stone-300 rounded-xl flex items-center justify-center overflow-hidden bg-stone-50">
                      {logoSrc ? (
                        <img data-testid="logo-preview" src={logoSrc} alt="Logo" className="w-full h-full object-contain" />
                      ) : (
                        <Upload className="w-6 h-6 text-stone-400" />
                      )}
                    </div>
                    <div>
                      <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp,.gif" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); e.target.value = ""; }} />
                      <Button
                        data-testid="upload-logo-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="text-sm"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                        {uploading ? "Uploading..." : logoSrc ? "Change Logo" : "Upload Logo"}
                      </Button>
                      <p className="text-xs text-stone-400 mt-1">PNG, JPG, SVG. Max 5MB.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-stone-500">Only the owner can change branding settings.</p>
            )}
          </CardContent>
        </Card>

        {/* Account */}
        <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardContent className="p-6">
            <h2 className="text-base font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-stone-500" /> Account
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b border-stone-100">
                <span className="text-stone-500">Name</span>
                <span data-testid="account-name" className="text-stone-900 font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-stone-100">
                <span className="text-stone-500">Email</span>
                <span data-testid="account-email" className="text-stone-900 font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-stone-500">Role</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isOwner ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-600"}`}>
                  {isOwner ? "Owner" : "Team Member"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Telegram Integration */}
        <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardContent className="p-6">
            <h2 className="text-base font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-stone-500" /> Telegram Integration
            </h2>
            <p className="text-xs text-stone-500 mb-4">
              Connect your Telegram to chat with the AI assistant on the go. Each team member can link their own account.
            </p>
            
            {telegramLink?.linked ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                      <Check className="w-4 h-4" /> Telegram Connected
                    </p>
                    {telegramLink.telegram_username && (
                      <p className="text-xs text-emerald-600 mt-0.5">@{telegramLink.telegram_username}</p>
                    )}
                  </div>
                  <Button
                    data-testid="unlink-telegram-btn"
                    variant="ghost"
                    size="sm"
                    onClick={unlinkTelegram}
                    className="text-emerald-700 hover:text-red-600 hover:bg-red-50"
                  >
                    <Unlink className="w-4 h-4 mr-1" /> Unlink
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-stone-700 mb-2">How to connect:</p>
                  <ol className="text-xs text-stone-600 space-y-1.5 list-decimal list-inside">
                    <li>Open Telegram and message <a href="https://t.me/FrdmCoffeeBot" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">@FrdmCoffeeBot</a></li>
                    <li>Send the command <code className="bg-stone-200 px-1.5 py-0.5 rounded">/link</code></li>
                    <li>Copy the code and paste it below</li>
                  </ol>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    data-testid="telegram-code-input"
                    value={telegramCode}
                    onChange={(e) => setTelegramCode(e.target.value.toUpperCase())}
                    placeholder="Enter code from Telegram"
                    className="flex-1 text-sm uppercase tracking-wider"
                    maxLength={8}
                  />
                  <Button
                    data-testid="link-telegram-btn"
                    onClick={linkTelegram}
                    disabled={linkingTelegram || !telegramCode.trim()}
                    className="bg-blue-600 text-white hover:bg-blue-700 w-full sm:w-auto"
                  >
                    {linkingTelegram ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Link Account
                  </Button>
                </div>
                
                {/* Alternative: Direct Link */}
                <div className="border-t border-stone-200 pt-3 mt-3">
                  <p className="text-xs text-stone-500 mb-2">
                    <strong>Having trouble?</strong> Use direct link instead:
                  </p>
                  <ol className="text-xs text-stone-500 space-y-1 list-decimal list-inside mb-2">
                    <li>Message <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">@userinfobot</a> on Telegram</li>
                    <li>Copy your "Id" number</li>
                    <li>Paste it below</li>
                  </ol>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      data-testid="telegram-chatid-input"
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value.replace(/\D/g, ''))}
                      placeholder="Your Telegram Chat ID"
                      className="flex-1 text-sm"
                    />
                    <Button
                      data-testid="direct-link-telegram-btn"
                      onClick={directLinkTelegram}
                      disabled={linkingTelegram || !telegramChatId.trim()}
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      {linkingTelegram ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      Direct Link
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invites (owner only) */}
        {isOwner && (
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-stone-500" /> Team Invites
              </h2>
              <p className="text-xs text-stone-500 mb-4">Generate invite codes to let team members join your Command Center.</p>
              <div className="flex gap-2 mb-4">
                <Input
                  data-testid="invite-email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Email (optional)"
                  className="flex-1 text-sm"
                />
                <Button
                  data-testid="create-invite-btn"
                  onClick={createInvite}
                  disabled={creating}
                  className="bg-stone-900 text-stone-50 hover:bg-stone-800 px-5"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Create
                </Button>
              </div>
              {invites.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-4">No invites yet.</p>
              ) : (
                <div className="space-y-2">
                  {invites.map((inv) => (
                    <div key={inv.id} data-testid={`invite-${inv.id}`} className="flex items-center gap-3 bg-stone-50 rounded-lg px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono font-semibold text-stone-900">{inv.code}</code>
                          {inv.used ? (
                            <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">Used{inv.used_by ? ` by ${inv.used_by}` : ""}</span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">Pending</span>
                          )}
                        </div>
                        {inv.email && <p className="text-xs text-stone-400 mt-0.5"><Mail className="w-3 h-3 inline mr-0.5" /> {inv.email}</p>}
                      </div>
                      {!inv.used && (
                        <Button
                          data-testid={`copy-invite-${inv.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => copyCode(inv.code, inv.id)}
                          className="text-stone-500 hover:text-stone-700 px-2"
                        >
                          {copiedId === inv.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                      <Button
                        data-testid={`delete-invite-${inv.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteInvite(inv.id)}
                        className="text-stone-400 hover:text-red-600 px-2"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
